import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import DataFlowV2Service, {
    getDataFlowV2FieldErrors,
} from "src/modules/data-protection/apis/DataFlowV2Service";
import { DATA_FLOW_ENDPOINTS_QUERY_KEY } from "./useDataFlowEndpoints";
import { invalidateSoftwareDataFlowsV2 } from "./useSoftwareDataFlowsV2";

const useUpdateDataFlow = ({ softwareId, t, onSuccess, onError } = {}) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async ({ id, payload, endpointUpdate }) => {
            const service = DataFlowV2Service.getInstance();

            if (endpointUpdate) {
                await service.updateEndpoint(endpointUpdate.id, {
                    type: endpointUpdate.type,
                    name: endpointUpdate.name,
                    identifier: endpointUpdate.identifier,
                });
            }

            return await service.updateDataFlow(id, payload);
        },
        onSuccess: (_data, variables) => {
            toast(t("Data flow updated successfully."), {
                type: "success",
            });

            invalidateSoftwareDataFlowsV2(queryClient);

            if (variables?.endpointUpdate) {
                queryClient.invalidateQueries({
                    queryKey: [DATA_FLOW_ENDPOINTS_QUERY_KEY],
                });
            }

            if (typeof onSuccess === "function") {
                onSuccess();
            }
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            const fieldErrors = getDataFlowV2FieldErrors(error);

            if (typeof onError === "function") {
                onError(fieldErrors);
            }

            if (!Object.keys(fieldErrors).length) {
                toast(t("Failed to update data flow."), {
                    type: "error",
                });
            }
        },
    });

    return {
        updateFlow: mutation.mutate,
        isSaving: mutation.isLoading,
    };
};

export default useUpdateDataFlow;
